import dayjs from 'dayjs'
import { RemoteLogger } from '@ally/federated-types'
import {
  StatusTrackerActivity,
  WireEvent,
  DebitCardOrderEvent,
} from '@ally/shared-status-tracker'
import { AchEvent } from './types'
import { SERVER_TIMEZONE } from '../../../constants'

export const checkOrderLR = (
  currentStatus: string,
  currentTime: string,
  previousStatus: string,
  previousTime: string,
  log: RemoteLogger,
): void => {
  const inProcess = currentStatus && currentStatus === 'InProcess'
  const confirmed = previousStatus && previousStatus === 'Confirmed'
  if (inProcess && confirmed) {
    const confirmedTime = previousTime
    const inProcessTime = currentTime
    if (dayjs(inProcessTime).diff(confirmedTime, 'hours') > 24) {
      log.track('status-tracker-checkorder-date-diff')
    }
  }
}

/**
 * Function that checks if ACH transfer is past deliverOnDate
 *
 * @param {string} dateString
 * @returns {boolean}
 *
 * @example
 * const deliverOnDateTime = '2020-09-02T10:58:29.486Z'
 */
export function isAfterDeliverOnDate(deliverOnDateTime: string): boolean {
  const today = dayjs().tz(SERVER_TIMEZONE)
  const deliverOnDate = dayjs(deliverOnDateTime).tz(SERVER_TIMEZONE)
  return today.isAfter(deliverOnDate, 'day')
}

// Status Tracker
function showWire(
  currentStatus: string,
  daysSinceStatusChange: number,
): boolean {
  if (currentStatus === 'Canceled' || currentStatus === 'Sent') {
    return daysSinceStatusChange <= 2
  }
  return true
}

function showCheckOrder(
  log: RemoteLogger,
  currentStatus: string,
  currentTime: string,
  daysSinceStatusChange: number,
  previousStatus?: string,
  previousTime?: string,
): boolean {
  checkOrderLR(currentStatus, currentTime, previousStatus!, previousTime!, log)
  if (currentStatus === 'Shipped') {
    return daysSinceStatusChange <= 7
  }
  return true
}

function showAch(
  currentStatus: string,
  daysSinceStatusChange: number,
): boolean {
  if (currentStatus === 'Completed') {
    return daysSinceStatusChange <= 2
  }
  // ACH returns many status' we do not need so only return what we care for
  return currentStatus === 'InProcess'
}

export const filterActivities = (
  activities: StatusTrackerActivity[],
  log: RemoteLogger,
  isDebitCardStatusTrackerEnabled: boolean,
): StatusTrackerActivity[] => {
  // Converting all ACH activities afterDeliverOnDate to 'Completed' for sorting purposes
  const mappedActivities = activities.map(activity => {
    const isAch = activity.activityName === 'ACHTransfer'
    const isWire = activity.activityName === 'WireTransfer'
    const isDebitCardOrder = activity.activityName === 'DebitCardOrder'
    const currentStatus = activity.statuses[0].status
    const today = dayjs().tz(SERVER_TIMEZONE)
    const deliverOnDateTime = (activity.eventModelData as AchEvent)
      .deliverOnDate
    const afterDeliverOnDate = isAfterDeliverOnDate(deliverOnDateTime)

    if (isAch && currentStatus === 'InProcess' && afterDeliverOnDate) {
      const deliverOnDatePlusOne = dayjs(deliverOnDateTime)
        .tz(SERVER_TIMEZONE)
        .add(1, 'day')
      const timeSinceStatusChange = today.diff(deliverOnDatePlusOne, 'day')

      return {
        ...activity,
        completed: true,
        timeSinceStatusChange,
        statuses: [{ ...activity.statuses[0], status: 'Completed' }],
      }
    }

    if (isWire) {
      const recipientRoutingNumber =
        (activity.eventModelData as WireEvent).recipientRoutingNumber || '--'

      return {
        ...activity,
        eventModelData: {
          ...activity.eventModelData,
          recipientRoutingNumber,
        } as WireEvent,
      }
    }

    if (isDebitCardOrder) {
      return {
        ...activity,
        eventModelData: {
          ...activity.eventModelData,
        } as DebitCardOrderEvent,
      }
    }

    return activity
  }) as StatusTrackerActivity[]

  return mappedActivities.filter((activity: StatusTrackerActivity) => {
    const currentStatus = activity.statuses[0].status
    const previousStatus = activity.statuses[1]?.status
    const currentTime = activity.statuses[0].eventDateTime
    const previousTime = activity.statuses[1]?.eventDateTime
    const daysSinceStatusChange = activity.timeSinceStatusChange

    switch (activity.activityName) {
      case 'WireTransfer':
        return showWire(currentStatus, daysSinceStatusChange)

      case 'CheckOrder':
        return showCheckOrder(
          log,
          currentStatus,
          currentTime,
          daysSinceStatusChange,
          previousStatus,
          previousTime,
        )

      case 'DebitCardOrder':
        return !!isDebitCardStatusTrackerEnabled
      case 'ACHTransfer':
      default:
        return showAch(currentStatus, daysSinceStatusChange)
    }
  })
}
